import type { BillingPeriod, Contract } from '@traceio-org/platform-contracts';

/**
 * The billing periods of a contract that have started, newest first, so a staff member can pick a period for its
 * usage or line items (platform-api answers one period per request and wants its exact start). The arithmetic is
 * the server's (services/platform-api/src/billing/periods.ts): the trial, when there is one, is a period of its
 * own; then months from the billing anchor on its day of the month and time of day in UTC, the last day of a
 * shorter month standing in for it; a contract that ends cuts its last period at its end. Only the list of starts
 * is derived here; every amount still comes from platform-api.
 */

type ContractWindow = Pick<Contract, 'startsAt' | 'endsAt' | 'trialEndsAt'>;

/** No contract has more periods than this; the loop stops there instead of running away on bad data. */
const MAX_PERIODS = 12 * 100;

/** The anchor moved by whole months, on its day of the month (the month's last day when it has fewer days). */
export function addMonths(anchor: Date, months: number): Date {
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(
    Date.UTC(
      year,
      month,
      Math.min(anchor.getUTCDate(), lastDay),
      anchor.getUTCHours(),
      anchor.getUTCMinutes(),
      anchor.getUTCSeconds(),
      anchor.getUTCMilliseconds(),
    ),
  );
}

function regularPeriod(window: ContractWindow, n: number): BillingPeriod | null {
  const anchor = new Date(window.trialEndsAt ?? window.startsAt);
  const end = window.endsAt === null ? null : Date.parse(window.endsAt);
  const start = addMonths(anchor, n);
  if (end !== null && start.getTime() >= end) return null;
  let periodEnd = addMonths(anchor, n + 1).getTime();
  if (end !== null && periodEnd > end) periodEnd = end;
  return { start: start.toISOString(), end: new Date(periodEnd).toISOString(), kind: 'regular' };
}

/** Every period of the contract that started by `now` (start <= now), newest first. */
export function startedPeriods(window: ContractWindow, now: number): BillingPeriod[] {
  const periods: BillingPeriod[] = [];
  if (Date.parse(window.startsAt) > now) return periods;
  if (window.trialEndsAt !== null) {
    periods.push({ start: window.startsAt, end: window.trialEndsAt, kind: 'trial' });
  }
  for (let n = 0; n < MAX_PERIODS; n += 1) {
    const period = regularPeriod(window, n);
    if (!period || Date.parse(period.start) > now) break;
    periods.push(period);
  }
  return periods.reverse();
}
